import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { useMemo } from 'react';
import { useProductDetails, useProductDetailsForArray } from '../product/useProductDetails';
import { useProductFromProductInventoryByApiName, useProductFromProductInventoryBySkuId, useProductInventory } from './useProductInventory';
import { useQuantityPackInfoFromProductInventory } from './useProductInventoryLookup';
export const useProductInventoryQuantityByApiName = ({
  apiName
}) => {
  var _product$skuQuantityP;
  const {
    product,
    loading,
    error
  } = useProductFromProductInventoryByApiName({
    apiName
  });
  const quantity = (_product$skuQuantityP = product === null || product === void 0 ? void 0 : product.skuQuantityPurchased) !== null && _product$skuQuantityP !== void 0 ? _product$skuQuantityP : 0;
  return {
    quantity,
    loading,
    error
  };
};
export const useProductInventoryQuantityBySkuId = ({
  skuId
}) => {
  var _product$skuQuantityP2;
  const {
    product,
    loading,
    error
  } = useProductFromProductInventoryBySkuId({
    skuId
  });
  const quantity = (_product$skuQuantityP2 = product === null || product === void 0 ? void 0 : product.skuQuantityPurchased) !== null && _product$skuQuantityP2 !== void 0 ? _product$skuQuantityP2 : 0;
  return {
    quantity,
    loading,
    error
  };
};
export const useHasMaxProductQuantityInProductInventory = ({
  apiName
}) => {
  const {
    products,
    loading: inventoryLoading,
    error
  } = useProductInventory({
    skip: !apiName
  });
  const {
    productDetails,
    loading: detailsLoading
  } = useProductDetails({
    productApiName: apiName
  });
  const inventoryProduct = products === null || products === void 0 ? void 0 : products.find(p => p.productApiName === apiName);
  const hasMaxQuantity = useMemo(() => {
    if (inventoryLoading || detailsLoading || !apiName) return false;
    if (!inventoryProduct) return false;
    const maxQuantity = productDetails === null || productDetails === void 0 ? void 0 : productDetails.maxTouchlessPurchasableQuantity;
    if (!maxQuantity && maxQuantity !== 0) return false;
    return inventoryProduct.skuQuantityPurchased === maxQuantity;
  }, [inventoryProduct, productDetails, apiName, inventoryLoading, detailsLoading]);
  const maxQuantity = inventoryLoading || detailsLoading ? undefined : productDetails === null || productDetails === void 0 ? void 0 : productDetails.maxTouchlessPurchasableQuantity;
  const ownedQuantity = inventoryLoading || detailsLoading ? undefined : inventoryProduct === null || inventoryProduct === void 0 ? void 0 : inventoryProduct.skuQuantityPurchased;
  return {
    hasMaxQuantity,
    maxQuantity,
    ownedQuantity,
    loading: inventoryLoading || detailsLoading,
    error
  };
};

/**
 * Replaces getProductsFromProductInventoryQuantityPackQuantity and productInventoryQuantityPackQuantitySelector
 * @param productApiName
 * @param quantityPackApiName
 * @returns {
 *  quantityPackQuantity: number,
 *  loading: boolean,
 *  error: ApolloError | undefined,
 * }
 */
export const useProductInventoryQuantityPackQuantity = ({
  productApiName,
  quantityPackApiName
}) => {
  var _quantityPackInfo$sku;
  const {
    quantityPackInfo,
    loading: quantityPackInfoLoading,
    error: quantityPackInfoError
  } = useQuantityPackInfoFromProductInventory({
    productApiName,
    quantityPackApiName
  });
  const quantityPackQuantity = (_quantityPackInfo$sku = quantityPackInfo === null || quantityPackInfo === void 0 ? void 0 : quantityPackInfo.skuQuantityPurchased) !== null && _quantityPackInfo$sku !== void 0 ? _quantityPackInfo$sku : 0;
  return {
    quantityPackQuantity,
    loading: quantityPackInfoLoading,
    error: quantityPackInfoError
  };
};

/**
 * Replaces quantityPackProductConfigurationsFromProductInventorySelector
 * @param baseProductConfigurations - The base product configurations to find quantity packs for
 * @returns {
 *  quantityPackProductConfigurations: ProductConfiguration[],
 *  loading: boolean,
 *  error: ApolloError | undefined,
 * }
 */
export const useQuantityPackProductConfigurationsFromProductInventory = ({
  baseProductConfigurations
}) => {
  const {
    products: inventoryProducts,
    loading: inventoryLoading,
    error: inventoryError
  } = useProductInventory();

  // Get API names for the base product configurations
  const productApiNames = useMemo(() => {
    if (!(baseProductConfigurations !== null && baseProductConfigurations !== void 0 && baseProductConfigurations.length) || !(inventoryProducts !== null && inventoryProducts !== void 0 && inventoryProducts.length)) return [];
    const apiNames = [];
    baseProductConfigurations.forEach(({
      skuId
    }) => {
      const inventoryProduct = inventoryProducts.find(p => p.skuId === skuId);
      if (inventoryProduct) {
        apiNames.push(inventoryProduct.productApiName);
      }
    });
    return [...new Set(apiNames)]; // Remove duplicates
  }, [baseProductConfigurations, inventoryProducts]);
  const {
    productDetails,
    loading: detailsLoading
  } = useProductDetailsForArray({
    productApiNames
  });
  const quantityPackProductConfigurations = useMemo(() => {
    if (inventoryLoading || detailsLoading || !inventoryProducts || !(productDetails !== null && productDetails !== void 0 && productDetails.productsByName) || !(baseProductConfigurations !== null && baseProductConfigurations !== void 0 && baseProductConfigurations.length)) {
      return [];
    }
    const configurations = [];
    baseProductConfigurations.forEach(({
      skuId
    }) => {
      var _inventoryProduct$qua, _productDetail$quanti;
      // Find the inventory product for this base configuration
      const inventoryProduct = inventoryProducts.find(p => p.skuId === skuId);
      if (!(inventoryProduct !== null && inventoryProduct !== void 0 && (_inventoryProduct$qua = inventoryProduct.quantityPackDetails) !== null && _inventoryProduct$qua !== void 0 && _inventoryProduct$qua.length)) return;
      const productDetail = productDetails.productsByName[inventoryProduct.productApiName];
      if (!(productDetail !== null && productDetail !== void 0 && (_productDetail$quanti = productDetail.quantityPacks) !== null && _productDetail$quanti !== void 0 && _productDetail$quanti.length)) return;
      inventoryProduct.quantityPackDetails.forEach(ownedQuantityPack => {
        // Find the matching quantity pack in product details by skuUuid
        const matchingQuantityPackDetail = productDetail.quantityPacks.find(qp => qp.skuUuid === ownedQuantityPack.skuUuid);
        if (matchingQuantityPackDetail) {
          var _matchingQuantityPack;
          // Calculate total quantity including multiplier effect
          const totalQuantity = ownedQuantityPack.skuQuantityPurchased * ((_matchingQuantityPack = matchingQuantityPackDetail.includedWithQuantityPack) !== null && _matchingQuantityPack !== void 0 ? _matchingQuantityPack : 1);
          if (totalQuantity > 0) {
            configurations.push(createProductConfig({
              skuId: matchingQuantityPackDetail.skuId,
              quantity: totalQuantity
            }));
          }
        }
      });
    });
    return configurations;
  }, [baseProductConfigurations, inventoryProducts, productDetails, inventoryLoading, detailsLoading]);
  return {
    quantityPackProductConfigurations,
    loading: inventoryLoading || detailsLoading,
    error: inventoryError
  };
};